import Button from '@/shared/Button'
import FormItem from '@/shared/FormCreator/FormItem'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Close from '@mui/icons-material/Close'

import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import unionBy from 'lodash/unionBy'
import moment from 'moment'
import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useForm } from 'react-hook-form'

import debounce from '@mui/material/utils/debounce'

const inputConfig = {
  inputBaseProps: {
    alignItems: 'center',
    childrenSx: {
      '& .MuiOutlinedInput-root': {
        background: 'white',
      },
    },
  },
}

const mapStateToProps = ({ loading }) => ({
  isUserGroupLoading: loading['userGroups/getUserGroups'],
  isSmsTemplateLabelsLoading: loading['smsTemplate/getSmsTemplateLabels'],
  isLandingPageLabelsLoading: loading['landingPage/getLandingPageLabels'],
})

const PhishCampaignSmsCreateGroupItem = forwardRef(
  (
    {
      multi = false,
      dispatch,
      index,
      isSmsTemplateLabelsLoading,
      isLandingPageLabelsLoading,
      isUserGroupLoading,
      onDelete,
      setPreviewSmsTemplateId,
    },
    ref,
  ) => {
    const { t } = useTranslation()

    const validations = {
      userGroup: Yup.object()
        .required(t('validation.user_group_required'))
        .typeError(t('validation.user_group_required')),
      startDate: Yup.date().minStartDate(
        t('validation.min_startdate'),
        t('validation.min_startdate2'),
      ),
      startTime: Yup.date().typeError('').minStartDate('', ''),
      endDate: Yup.date().afterStartDate(t('validation.after_startdate')),
      endTime: Yup.date().typeError('').afterStartDate(''),
      // smsTemplate: Yup.object()
      //   .nullable()
      //   .smsTemplate(t('validation.sms_template_required'))
      //   .typeError(t('validation.sms_template_required')),
      // smsTemplateLabel: Yup.object()
      //   .nullable()
      //   .smsTemplateLabel(t('validation.sms_template_label_required'))
      //   .typeError(t('validation.sms_template_label_required')),
      landingPage: Yup.object()
        .nullable()
        .landingPage(t('validation.landing_page_required'))
        .typeError(t('validation.landing_page_required')),
    }

    if (multi) {
      validations.name = Yup.string().required(t('validation.campaign_name_required'))
    }

    const form = useForm({
      resolver: yupResolver(Yup.object().shape(validations)),
    })

    const userGroupsSearchInputRef = useRef()
    const smsTemplateLabelsSearchInputRef = useRef()
    const smsTemplatesSearchInputRef = useRef()
    const landingPageLabelsSearchInputRef = useRef()
    const landingPagesSearchInputRef = useRef()

    const [userGroupsSearchInput, setUserGroupsSearchInput] = useState('')
    const [userGroupsSearchResults, setUserGroupsSearchResults] = useState({})
    const [userGroupsPage, setUserGroupsPage] = useState(0)

    const [smsTemplatesSearchInput, setSmsTemplatesSearchInput] = useState('')
    const [smsTemplatesSearchResults, setSmsTemplatesSearchResults] = useState({})
    const [smsTemplatesPage, setSmsTemplatesPage] = useState(0)

    const [landingPagesSearchInput, setLandingPagesSearchInput] = useState('')
    const [landingPagesSearchResults, setLandingPagesSearchResults] = useState({})
    const [landingPagesPage, setLandingPagesPage] = useState(0)

    const [smsTemplateLabelsSearchInput, setSmsTemplateLabelsSearchInput] = useState('')
    const [smsTemplateLabelsSearchResults, setSmsTemplateLabelsSearchResults] = useState({})
    const [smsTemplateLabelsPage, setSmsTemplateLabelsPage] = useState(0)

    const [landingPageLabelsSearchInput, setLandingPageLabelsSearchInput] = useState('')
    const [landingPageLabelsSearchResults, setLandingPageLabelsSearchResults] = useState({})
    const [landingPageLabelsPage, setLandingPageLabelsPage] = useState(0)

    // const [smsTemplatePreview, setSmsTemplatePreview] = useState()
    // const [landingPagePreview, setLandingPagePreview] = useState()
    // const [showLandingPagePreview, setShowLandingPagePreview] = useState(false)

    const {
      control,
      watch,
      setValue,
      // eslint-disable-next-line no-unused-vars
      formState: { errors },
    } = form

    useImperativeHandle(ref, () => ({
      form,
    }))

    const isStartTimeFixed = isEqual(watch('dateRangeType'), '1')

    const phishType = watch('phishType')
    const phishSmsLabelType = watch('phishSmsLabelType')
    const landingType = watch('landingType')
    const smsTemplate = watch('smsTemplate')
    const smsTemplateLabel = watch('smsTemplateLabel')
    const landingPage = watch('landingPage')
    const landingPageLabel = watch('landingPageLabel')

    const isPhishRandom = phishType === 'R'
    const isLandingDefault = landingType === 'default'

    const { data: userGroupsSearchResultsData = [], paging: userGroupsSearchResultsPaging } =
      userGroupsSearchResults

    const { data: smsTemplatesSearchResultsData = [], paging: smsTemplatesSearchResultsPaging } =
      smsTemplatesSearchResults

    const { data: landingPagesSearchResultsData = [], paging: landingPagesSearchResultsPaging } =
      landingPagesSearchResults

    const {
      data: smsTemplateLabelsSearchResultsData = [],
      paging: smsTemplateLabelsSearchResultsPaging,
    } = smsTemplateLabelsSearchResults

    const {
      data: landingPageLabelsSearchResultsData = [],
      paging: landingPageLabelsSearchResultsPaging,
    } = landingPageLabelsSearchResults

    // fetch user groups START
    const fetchUserGroups = useCallback(() => {
      dispatch({
        type: 'userGroup/getUserGroups',
        payload: {
          pageIndex: userGroupsPage,
          pageSize: 100,
          searchGroupName: userGroupsSearchInputRef.current,
        },
        successCallback: response => {
          const { data: responseData = [], paging: responsePaging = {} } = response || {}
          const { pageIndex } = responsePaging

          const newData = responseData.map(d => ({ id: d.groupId, label: d.groupName }))

          setUserGroupsSearchResults(s => ({
            data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
            paging: responsePaging,
          }))
        },
      })
    }, [dispatch, userGroupsPage])

    const loadMoreUserGroupsDebounce = debounce(() => {
      setUserGroupsPage(p => p + 1)
    }, 400)

    const fetchUserGroupsDebounce = useMemo(
      () =>
        debounce(() => {
          fetchUserGroups()
        }, 400),
      [fetchUserGroups],
    )

    useEffect(() => {
      fetchUserGroupsDebounce()
    }, [userGroupsSearchInput, userGroupsPage, fetchUserGroupsDebounce])
    // fetch user groups END

    // fetch sms template labels START
    const fetchSmsTemplateLabels = useCallback(() => {
      dispatch({
        type: 'smsTemplate/getSmsTemplateLabels',
        payload: {
          type: isPhishRandom ? phishSmsLabelType : phishType,
          searchParam: smsTemplateLabelsSearchInputRef.current,
          pageIndex: smsTemplateLabelsPage,
          pageSize: 100,
        },
        successCallback: response => {
          const { data: responseData, paging: responsePaging } = response
          const { pageIndex } = responsePaging

          const newData = responseData.map(d => ({ id: d.labelId, label: d.labelName }))

          setSmsTemplateLabelsSearchResults(s => ({
            data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
            paging: responsePaging,
          }))
        },
      })
    }, [dispatch, phishType, phishSmsLabelType, isPhishRandom, smsTemplateLabelsPage])

    const loadMoreSmsTemplateLabelsPagesDebounce = debounce(() => {
      setSmsTemplateLabelsPage(p => p + 1)
    }, 400)

    const fetchSmsTemplateLabelsDebounce = useMemo(
      () =>
        debounce(() => {
          fetchSmsTemplateLabels()
        }, 400),
      [fetchSmsTemplateLabels],
    )

    useEffect(() => {
      fetchSmsTemplateLabelsDebounce()
    }, [smsTemplateLabelsSearchInput, fetchSmsTemplateLabelsDebounce, smsTemplateLabelsPage])
    // fetch sms template labels END

    // fetch sms templates START
    const fetchSmsTemplates = useCallback(() => {
      dispatch({
        type: 'smsTemplate/getSmsTemplates',
        payload: {
          pageIndex: smsTemplatesPage,
          pageSize: 100,
          searchParam: smsTemplatesSearchInputRef.current,
          sort: 'SMS_NAME_ASC',
          type: isPhishRandom ? phishSmsLabelType : phishType,
          labelId: smsTemplateLabel?.id,
        },
        successCallback: response => {
          const { data: responseData = [], paging: responsePaging = {} } = response || {}
          const { pageIndex } = responsePaging
          const newData = responseData.map(d => ({ id: d.smsTempId, label: d.name, data: d }))

          setSmsTemplatesSearchResults(s => ({
            data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
            paging: responsePaging,
          }))
        },
      })
    }, [dispatch, smsTemplatesPage, phishType, phishSmsLabelType, smsTemplateLabel, isPhishRandom])

    const loadMoreSmsTemplatesDebounce = debounce(() => {
      setSmsTemplatesPage(p => p + 1)
    }, 400)

    const fetchSmsTemplatesDebounce = useMemo(
      () =>
        debounce(() => {
          fetchSmsTemplates()
        }, 400),
      [fetchSmsTemplates],
    )

    useEffect(() => {
      fetchSmsTemplatesDebounce()
    }, [smsTemplatesSearchInput, smsTemplatesPage, fetchSmsTemplatesDebounce])
    // fetch sms templates END

    // sms templates clean up START
    useEffect(() => {
      setSmsTemplatesSearchResults({})
      setSmsTemplatesPage(0)

      setSmsTemplateLabelsSearchResults({})
      setSmsTemplateLabelsPage(0)
      setValue('smsTemplateLabel', '')
      setValue('smsTemplate', '')
    }, [phishType, phishSmsLabelType, setValue])
    // sms templates clean up END

    // fetch landing page labels START
    const fetchLandingPageLabels = useCallback(() => {
      if (!isLandingDefault) {
        dispatch({
          type: 'landingPage/getLandingPageLabels',
          payload: {
            type: landingType,
            searchLabelName: landingPageLabelsSearchInputRef.current,
            pageIndex: landingPageLabelsPage,
            pageSize: 100,
          },
          successCallback: response => {
            const { data: responseData, paging: responsePaging } = response
            const { pageIndex } = responsePaging

            const newData = responseData.map(d => ({
              id: d.landingpageLabelId,
              label: d.labelName,
            }))

            setLandingPageLabelsSearchResults(s => ({
              data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
              paging: responsePaging,
            }))
          },
        })
      }
    }, [dispatch, landingType, isLandingDefault, landingPageLabelsPage])

    const loadMoreLandingPageLabelsPagesDebounce = debounce(() => {
      setLandingPageLabelsPage(p => p + 1)
    }, 400)

    const fetchLandingPageLabelsDebounce = useMemo(
      () =>
        debounce(() => {
          fetchLandingPageLabels()
        }, 400),
      [fetchLandingPageLabels],
    )

    useEffect(() => {
      fetchLandingPageLabelsDebounce()
    }, [landingPageLabelsSearchInput, fetchLandingPageLabelsDebounce, landingPageLabelsPage])
    // fetch landing page labels END

    // fetch landing pages START
    const fetchLandingPages = useCallback(() => {
      if (!isLandingDefault) {
        dispatch({
          type: 'landingPage/getLandingPages',
          payload: {
            pageIndex: landingPagesPage,
            pageSize: 100,
            searchParam: landingPagesSearchInputRef.current,
            sort: 'LANDINGPAGE_NAME_ASC',
            type: landingType,
            labelId: landingPageLabel?.id,
          },
          successCallback: response => {
            const { data: responseData = [], paging: responsePaging = {} } = response || {}
            const { pageIndex } = responsePaging

            const newData = responseData.map(d => ({
              id: d.landingpageTempId,
              label: d.name,
              data: d,
            }))

            setLandingPagesSearchResults(s => ({
              data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
              paging: responsePaging,
            }))
          },
        })
      }
    }, [dispatch, landingPagesPage, landingType, landingPageLabel, isLandingDefault])

    const loadMoreLandingPagesDebounce = debounce(() => {
      setLandingPagesPage(p => p + 1)
    }, 400)

    const fetchLandingPagesDebounce = useMemo(
      () =>
        debounce(() => {
          fetchLandingPages()
        }, 400),
      [fetchLandingPages],
    )

    useEffect(() => {
      fetchLandingPagesDebounce()
    }, [landingPagesSearchInput, landingPagesPage, fetchLandingPagesDebounce])
    // fetch landing pages END

    // landing page clean up START
    useEffect(() => {
      setLandingPagesSearchResults({})
      setLandingPagesPage(0)

      setLandingPageLabelsSearchResults({})
      setLandingPageLabelsPage(0)
      setValue('landingPageLabel', '')
      setValue('landingPage', '')
    }, [landingType, setValue])
    // landing page clean up END

    useEffect(() => {
      if (isStartTimeFixed) {
        setValue?.('startDate', moment().toDate())
        setValue?.('startTime', moment().toDate())

        setValue?.('endDate', moment().add(1, 'days').toDate())
        setValue?.('endTime', moment().add(1, 'days').toDate())
      } else {
        setValue?.('startDate', moment().add(20, 'minutes').toDate())
        setValue?.('startTime', moment().add(20, 'minutes').toDate())

        setValue?.('endDate', moment().add(20, 'minutes').add(1, 'days').toDate())
        setValue?.('endTime', moment().add(20, 'minutes').add(1, 'days').toDate())
      }
    }, [isStartTimeFixed, setValue])

    return (
      <Grid
        container
        spacing={4}
        sx={{
          position: 'relative',
          background: multi && index % 2 === 0 ? '#2D2D6C1A' : 'transparent',
          p: 3,
          mt: -1,
          pb: 5,
        }}
      >
        {index >= 2 && (
          <Box sx={{ position: 'absolute', top: 30, right: 20 }}>
            <IconButton
              onClick={() => {
                onDelete?.()
              }}
            >
              <Close />
            </IconButton>
          </Box>
        )}
        {multi && (
          <Grid item xs={12} sm={6}>
            <FormItem
              label={t('general.child_campaign_name')}
              defaultValue=""
              name="name"
              control={control}
              inputConfig={{ ...inputConfig, inputProps: { maxLength: 30 } }}
              errors={errors}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={multi ? 6 : 12}>
          <FormItem
            label={t('general.select_group')}
            name="userGroup"
            type="autocomplete"
            defaultValue=""
            control={control}
            errors={errors}
            inputConfig={{
              ...inputConfig,
              onInputChange: (_, value, reason) => {
                userGroupsSearchInputRef.current = value

                if (['input', 'clear'].includes(reason)) {
                  setUserGroupsPage(0)
                  setUserGroupsSearchInput(value)
                }
              },
              onFocus: () => {
                fetchUserGroupsDebounce()
              },
              onLoadMore: () => {
                const { hasNext } = userGroupsSearchResultsPaging

                if (!isUserGroupLoading && hasNext) {
                  loadMoreUserGroupsDebounce()
                }
              },
              options: userGroupsSearchResultsData,
              filterOptions: opts => opts,
              isOptionEqualToValue: (option, value) => option.id === value.id,
              renderOption: (renderProps, option) => (
                <Box component="li" {...renderProps} key={option.id}>
                  {option.label}
                </Box>
              ),
            }}
          />
        </Grid>

        {/* second part for event start and end time */}
        <Grid item xs={12}>
          <Stack direction={'row'} spacing={2} alignItems={'center'}>
            <FormItem
              defaultValue={2}
              label={t('general.event_time')}
              name="dateRangeType"
              type="radio"
              control={control}
              inputConfig={{
                options: [
                  { value: 1, label: t('general.now', { num: 1 }) },
                  { value: 2, label: t('general.custom_time', { num: 2 }) },
                ],
              }}
              errors={errors}
            />

            <FormItem
              disabled={isStartTimeFixed}
              defaultValue={moment().startOf('day').toDate()}
              name="startDate"
              type="date"
              control={control}
              inputConfig={{
                ...inputConfig,
                sx: {
                  width: 180,
                  '& .MuiFormHelperText-root.Mui-error': {
                    wordBreak: 'keep-all',
                    whiteSpace: 'nowrap',
                  },
                },
              }}
              errors={errors}
            />

            <FormItem
              disabled={isStartTimeFixed}
              defaultValue={new Date()}
              name="startTime"
              type="time"
              control={control}
              inputConfig={{
                ...inputConfig,
                sx: { width: 160 },
              }}
              errors={errors}
            />

            <Typography>~</Typography>

            <FormItem
              defaultValue={moment().startOf('day').toDate()}
              name="endDate"
              type="date"
              control={control}
              inputConfig={{
                ...inputConfig,
                sx: {
                  width: 180,
                  '& .MuiFormHelperText-root.Mui-error': { wordBreak: 'keep-all' },
                  whiteSpace: 'nowrap',
                },
              }}
              errors={errors}
            />

            <FormItem
              defaultValue={new Date()}
              name="endTime"
              type="time"
              control={control}
              inputConfig={{
                ...inputConfig,
                sx: { width: 160 },
              }}
              errors={errors}
            />
          </Stack>
        </Grid>

        {/* third part that is select sending time */}
        <Grid item xs={12} md={6}>
          <FormItem
            defaultValue={'R'}
            label={t('general.select_phish_sms')}
            name="phishType"
            type="radio"
            control={control}
            inputConfig={{
              ...inputConfig,
              onChange: val => {
                if (get(val, 'target.value', '') === 'R') {
                  setValue?.('landingType', 'default')
                }
              },
              options: [
                { value: 'R', label: t('general.random') },
                { value: 'S', label: t('menu.system') },
                { value: 'I', label: t('menu.individual') },
              ],
            }}
            errors={errors}
          />

          <Stack direction="row" spacing={1} mt={2}>
            {isPhishRandom && (
              <FormItem
                defaultValue="S"
                name="phishSmsLabelType"
                type="select"
                control={control}
                inputConfig={{
                  ...inputConfig,
                  sx: { width: 120 },
                  options: [
                    { value: 'S', label: t('menu.system') },
                    { value: 'I', label: t('menu.individual') },
                  ],
                }}
                errors={errors}
              />
            )}

            <FormItem
              name="smsTemplateLabel"
              type="autocomplete"
              defaultValue=""
              control={control}
              errors={errors}
              inputConfig={{
                ...inputConfig,
                placeholder: t('general.select_label'),
                clearOnBlur: true,
                onInputChange: (_, value) => {
                  smsTemplateLabelsSearchInputRef.current = value
                  setSmsTemplateLabelsSearchInput(value)
                },
                onFocus: () => {
                  fetchSmsTemplateLabelsDebounce()
                },
                onLoadMore: () => {
                  const { hasNext } = smsTemplateLabelsSearchResultsPaging

                  if (!isSmsTemplateLabelsLoading && hasNext) {
                    loadMoreSmsTemplateLabelsPagesDebounce()
                  }
                },
                options: smsTemplateLabelsSearchResultsData,
                filterOptions: opts => opts,
                isOptionEqualToValue: (option, value) => option.id === value.id,
                renderOption: (renderProps, option) => (
                  <Box component="li" {...renderProps} key={option.id}>
                    {option.label}
                  </Box>
                ),
              }}
            />

            {!isPhishRandom && (
              <FormItem
                name="smsTemplate"
                type="autocomplete"
                defaultValue=""
                control={control}
                errors={errors}
                inputConfig={{
                  ...inputConfig,
                  placeholder: t('general.select_template'),
                  onInputChange: (_, value, reason) => {
                    smsTemplatesSearchInputRef.current = value

                    if (['input', 'clear'].includes(reason)) {
                      setSmsTemplatesPage(0)
                      setSmsTemplatesSearchInput(value)
                    }
                  },
                  onFocus: () => {
                    fetchSmsTemplatesDebounce()
                  },
                  onLoadMore: () => {
                    const { hasNext } = smsTemplatesSearchResultsPaging

                    if (!isUserGroupLoading && hasNext) {
                      loadMoreSmsTemplatesDebounce()
                    }
                  },
                  options: smsTemplatesSearchResultsData,
                  filterOptions: opts => opts,
                  isOptionEqualToValue: (option, value) => option.id === value.id,
                  renderOption: (renderProps, option) => (
                    <Box component="li" {...renderProps} key={option.id}>
                      {option.label}
                    </Box>
                  ),
                }}
              />
            )}

            {!isPhishRandom && (
              <Button
                disabled={!smsTemplate}
                color="secondary_80"
                variant="outlined"
                onClick={() => {
                  setPreviewSmsTemplateId(smsTemplate?.id)
                }}
              >
                {t('general.preview')}
              </Button>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormItem
            defaultValue={'default'}
            label={t('general.select_landing_page')}
            name="landingType"
            type="radio"
            control={control}
            inputConfig={{
              ...inputConfig,
              options: [
                { value: 'default', label: t('general.preset'), disabled: isPhishRandom },
                { value: 'S', label: t('menu.system'), disabled: isPhishRandom },
                { value: 'I', label: t('menu.individual'), disabled: isPhishRandom },
              ],
            }}
            errors={errors}
          />

          {!isPhishRandom && (
            <Stack direction="row" spacing={1} mt={2}>
              {isLandingDefault ? (
                <FormItem
                  defaultValue=""
                  disabled={true}
                  name="defaultLanding"
                  control={control}
                  inputConfig={{ ...inputConfig, inputProps: { maxLength: 40 } }}
                  errors={errors}
                />
              ) : (
                <>
                  <FormItem
                    name="landingPageLabel"
                    type="autocomplete"
                    defaultValue=""
                    control={control}
                    errors={errors}
                    inputConfig={{
                      ...inputConfig,
                      placeholder: t('general.select_label'),
                      clearOnBlur: true,
                      onInputChange: (_, value) => {
                        landingPageLabelsSearchInputRef.current = value
                        setLandingPageLabelsSearchInput(value)
                      },
                      onFocus: () => {
                        fetchLandingPageLabelsDebounce()
                      },
                      onLoadMore: () => {
                        const { hasNext } = landingPageLabelsSearchResultsPaging

                        if (!isLandingPageLabelsLoading && hasNext) {
                          loadMoreLandingPageLabelsPagesDebounce()
                        }
                      },
                      options: landingPageLabelsSearchResultsData,
                      filterOptions: opts => opts,
                      isOptionEqualToValue: (option, value) => option.id === value.id,
                      renderOption: (renderProps, option) => (
                        <Box component="li" {...renderProps} key={option.id}>
                          {option.label}
                        </Box>
                      ),
                    }}
                  />

                  <FormItem
                    name="landingPage"
                    type="autocomplete"
                    defaultValue=""
                    control={control}
                    errors={errors}
                    inputConfig={{
                      ...inputConfig,
                      placeholder: t('general.select_template'),
                      onInputChange: (_, value, reason) => {
                        landingPagesSearchInputRef.current = value

                        if (['input', 'clear'].includes(reason)) {
                          setLandingPagesPage(0)
                          setLandingPagesSearchInput(value)
                        }
                      },
                      onFocus: () => {
                        fetchLandingPagesDebounce()
                      },
                      onLoadMore: () => {
                        const { hasNext } = landingPagesSearchResultsPaging

                        if (!isUserGroupLoading && hasNext) {
                          loadMoreLandingPagesDebounce()
                        }
                      },
                      options: landingPagesSearchResultsData,
                      filterOptions: opts => opts,
                      isOptionEqualToValue: (option, value) => option.id === value.id,
                      renderOption: (renderProps, option) => (
                        <Box component="li" {...renderProps} key={option.id}>
                          {option.label}
                        </Box>
                      ),
                    }}
                  />
                </>
              )}
              <Button
                disabled={!landingPage}
                color="secondary_80"
                variant="outlined"
                onClick={() => {
                  dispatch({
                    type: 'landingPage/getLandingPageHtml',
                    payload: { landingpageTempId: landingPage.data.landingpageTempId },
                    successCallback: html => {
                      const w = window.open('', '_blank', '') // access the "about:blank" window you've opened
                      w.document.body.innerHTML = html
                    },
                  })
                }}
              >
                {t('general.preview')}
              </Button>
            </Stack>
          )}
        </Grid>
      </Grid>
    )
  },
)

export default connect(mapStateToProps, null, null, { forwardRef: true })(
  PhishCampaignSmsCreateGroupItem,
)
